import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../hooks/useSocket';
import { toggleThemeMode } from '../../store/uiSlice';
import { colors } from '../../theme';
import * as dashboardService from '../../services/dashboardService';
import NotificationBell from './NotificationBell';
import UsageMenu from './UsageMenu';

function initials(str) {
  if (!str) return '?';
  return str
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

const iconBtnSx = { color: colors.aa.bodyText, width: 34, height: 34 };

export default function Header() {
  const { user, company, logout } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeMode = useSelector((state) => state.ui.themeMode);
  const { connected } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    let alive = true;
    dashboardService
      .getOverview()
      .then((res) => alive && setOverview(res.data.overview))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login');
  };

  const companyName = overview?.company?.name || company?.name || '';
  const wa = overview?.whatsapp;
  const waConnected = !!wa && wa.connected > 0;
  const planName = overview?.plan?.name;

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.default' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 64, px: { xs: 2, md: 3 }, py: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
          <Avatar
            sx={{ width: 36, height: 36, fontSize: 12.5, fontWeight: 700, bgcolor: colors.emerald[100], color: colors.emerald[700] }}
          >
            {initials(companyName)}
          </Avatar>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: colors.aa.headText }}>
            {companyName || 'Workspace'}
          </Typography>
          {wa && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ display: { xs: 'none', md: 'flex' }, pl: 1.5, ml: 0.5, borderLeft: `1px solid ${colors.aa.borderStrong}` }}
            >
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: waConnected ? colors.emerald[600] : colors.amber[600] }}
              />
              <Typography noWrap sx={{ fontSize: 13, color: colors.aa.bodyText }}>
                {wa.label || 'WhatsApp'} · {waConnected ? 'Connected' : wa.total ? 'Not verified' : 'Not configured'}
              </Typography>
            </Stack>
          )}
          {planName && (
            <Box
              sx={{
                px: 1.25,
                py: 0.4,
                borderRadius: '8px',
                bgcolor: colors.aa.headText,
                color: '#fff',
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {planName}
            </Box>
          )}
        </Stack>

        <Box sx={{ flexGrow: 1 }} />

        {user?.isPlatformOwner && (
          <Button
            component={RouterLink}
            to="/platform"
            size="small"
            startIcon={<AdminPanelSettingsRoundedIcon fontSize="small" />}
            sx={{ color: 'text.secondary', fontSize: 12 }}
          >
            Platform Admin
          </Button>
        )}

        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <UsageMenu />
        </Box>

        <Tooltip title={connected ? 'Live updates connected' : 'Live updates offline'}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: connected ? colors.emerald[600] : colors.slate[400],
              mx: 0.5,
            }}
          />
        </Tooltip>

        <NotificationBell />

        <Tooltip title="Help & support">
          <IconButton component={RouterLink} to="/support" sx={iconBtnSx}>
            <HelpOutlineRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={() => dispatch(toggleThemeMode())} sx={iconBtnSx}>
            {themeMode === 'dark' ? <LightModeOutlinedIcon sx={{ fontSize: 20 }} /> : <DarkModeOutlinedIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>

        <Tooltip title={user?.name || user?.email || 'Account'}>
          <Avatar
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ width: 34, height: 34, ml: 0.5, fontSize: 12, fontWeight: 700, bgcolor: colors.emerald[100], color: colors.emerald[700], cursor: 'pointer' }}
          >
            {initials(user?.name || user?.email)}
          </Avatar>
        </Tooltip>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{user?.name || 'Account'}</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{user?.email}</Typography>
          </Box>
          <MenuItem onClick={handleLogout} sx={{ fontSize: 13, gap: 1.25 }}>
            <LogoutRoundedIcon fontSize="small" />
            Logout
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
}
